/**
 * #2413 — "has someone already started initialising the SDK?", as a slot that
 * can be GIVEN BACK.
 *
 * WHY THIS IS A MODULE AND NOT A `let` IN THE COMPONENT. It was a bare module
 * flag set on SCHEDULE and never cleared, and PR review found what that costs:
 * React Strict Mode mounts an effect, runs its cleanup, and mounts again. The
 * first mount set the flag and scheduled the deferred init; the cleanup
 * cancelled the callback but left the flag standing; the second mount returned
 * early on it. `registerPostHog` was never called, so every event queued
 * forever and nothing reached PostHog — in exactly the environment where you
 * would go to verify that the queue works. A genuine unmount/remount before the
 * idle callback fires does the same in production.
 *
 * The decision lives here, away from the component, for the reason this repo
 * keeps relearning: a React effect is not reachable from the plain node test
 * runner, so a lifecycle rule written inline can only ever be pinned by a
 * regex on the component's source — and a regex on text is satisfiable by text.
 * As a module it is driven by a fixture that REPLAYS the mount/cleanup/mount
 * sequence, so the assertion fails when the behaviour breaks rather than when
 * the wording changes.
 *
 * THE ONE INVARIANT: the slot may be released up to, and only up to, the moment
 * `posthog.init()` is actually called. Before that, nothing has touched the SDK
 * and a retry is correct. After it, the slot must stay taken forever — a second
 * `init()` on the same singleton is the double-initialisation this guard exists
 * to prevent, and it is not undone by unmounting.
 */

let slotTaken = false;

/**
 * Claim the right to initialise. `true` means this caller should schedule the
 * deferred init; `false` means someone else already holds the slot.
 */
export function acquireInitSlot(): boolean {
  if (slotTaken) return false;
  slotTaken = true;
  return true;
}

/**
 * Give the slot back after a schedule was cancelled BEFORE `posthog.init()` ran.
 *
 * Callers must not call this once init has been invoked; see the invariant
 * above. The asymmetry is deliberate and is the whole point of the module —
 * `acquire` is cheap, `release` is conditional on a fact only the caller knows.
 */
export function releaseInitSlot(): void {
  slotTaken = false;
}

/** Test seam: reset module state between cases. Not used by app code. */
export function __resetInitGuardForTests(): void {
  slotTaken = false;
}
